import { useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Sparkles, ScrollText, Users, Layers3, Eye, Settings2, X } from "lucide-react";

const CHALLENGES = [
  {
    key: "ai-security", Icon: Sparkles, title: "AI Security",
    body: "As organizations adopt AI-powered tools and workflows, the attack surface now extends to models, agents, prompts, identities, and data — creating risks that traditional controls were never designed to address.",
  },
  {
    key: "compliance", Icon: ScrollText, title: "Regulatory & Compliance",
    body: "The regulatory landscape continues to evolve alongside emerging technologies. Organizations face growing pressure to interpret new requirements, demonstrate compliance, and maintain evidence.",
  },
  {
    key: "skill-gap", Icon: Users, title: "Security Skill Gap",
    body: "The global shortage of cybersecurity talent makes it increasingly difficult to hire and retain the expertise needed to manage complex security environments and respond to threats.",
  },
  {
    key: "tool-sprawl", Icon: Layers3, title: "Tool Sprawl",
    body: "An excessive number of disconnected security tools create operational inefficiencies, increase costs, and reduce overall security effectiveness.",
  },
  {
    key: "visibility", Icon: Eye, title: "Visibility Gap",
    body: "Fragmented systems limit visibility across the IT environment, slowing threat detection, incident response, and security decision-making.",
  },
  {
    key: "configuration", Icon: Settings2, title: "Configuration Gap",
    body: "Incorrect or insecure configurations across cloud, applications, networks, and identities can expose systems and sensitive data — creating openings for attackers.",
  },
];

function directionFromEvent(e, el) {
  const rect = el.getBoundingClientRect();
  const x = ((e.clientX - rect.left) / rect.width) - 0.5;
  const y = ((e.clientY - rect.top) / rect.height) - 0.5;
  if (Math.abs(x) > Math.abs(y)) return x > 0 ? "right" : "left";
  return y > 0 ? "bottom" : "top";
}
function offsetFor(dir, sign) {
  const s = sign;
  switch (dir) {
    case "top":    return { tx: 0,        ty: -100 * s };
    case "bottom": return { tx: 0,        ty:  100 * s };
    case "left":   return { tx: -100 * s, ty: 0        };
    case "right":  return { tx:  100 * s, ty: 0        };
    default:       return { tx: 0,        ty: -100 * s };
  }
}

function isTouch() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(hover: none)").matches;
}

function DirectionalCard({ item, i, onOpenModal }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const [state, setState] = useState({ hovered: false, tx: 0, ty: -100 });
  const { Icon } = item;

  const handleEnter = (e) => {
    if (reduced) { setState({ hovered: true, tx: 0, ty: 0 }); return; }
    const dir = directionFromEvent(e, ref.current);
    const from = offsetFor(dir, 1);
    // Snap to FROM position, then next-frame animate to 0/0
    setState({ hovered: false, tx: from.tx, ty: from.ty });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setState({ hovered: true, tx: 0, ty: 0 });
    }));
  };
  const handleLeave = (e) => {
    if (reduced) { setState({ hovered: false, tx: 0, ty: -100 }); return; }
    const dir = directionFromEvent(e, ref.current);
    const to = offsetFor(dir, 1);
    setState({ hovered: false, tx: to.tx, ty: to.ty });
  };
  const handleClick = () => {
    if (isTouch()) onOpenModal(item);
  };

  const panelStyle = {
    transform: `translate(${state.tx}%, ${state.ty}%)`,
    transition: "transform 520ms cubic-bezier(0.22,1,0.36,1)",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 }}
      className="relative aspect-square w-full"
    >
      <button
        type="button"
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={() => setState({ hovered: true, tx: 0, ty: 0 })}
        onBlur={() => setState({ hovered: false, tx: 0, ty: -100 })}
        onClick={handleClick}
        data-testid={`challenge-card-${item.key}`}
        aria-label={item.title}
        className="rl-card relative w-full h-full text-left"
      >
        {/* Front content — hides on hover so it never overlaps the panel */}
        <div
          className="absolute inset-0 z-10 p-5 md:p-7 flex flex-col justify-between transition-all duration-300"
          style={{
            opacity: state.hovered ? 0 : 1,
            transform: state.hovered ? "translateY(-8px)" : "translateY(0)",
            transitionDelay: state.hovered ? "0ms" : "150ms",
            pointerEvents: state.hovered ? "none" : "auto",
          }}
        >
          <div className="flex items-start justify-between">
            <span className="font-editorial-mono text-[10.5px] text-[#5B2A86]">0{i + 1}</span>
            <span className="h-9 w-9 rounded-full grid place-items-center border border-[rgba(17,17,17,0.08)] text-[#111]">
              <Icon strokeWidth={1.4} className="h-4 w-4" />
            </span>
          </div>
          <div>
            <h3 className="font-editorial-display text-[26px] md:text-[32px] leading-[1.02] tracking-[-0.02em] text-[#111]">
              {item.title}
            </h3>
            <div className="mt-4 flex items-center gap-1.5 text-[#5B2A86]">
              <span className="font-editorial-mono text-[10.5px]">EXPAND</span>
              <ArrowUpRight className="h-3.5 w-3.5" />
            </div>
          </div>
        </div>

        {/* Bottom-growing hairline (front decoration) */}
        <span className="rl-card__underline z-10" />

        {/* Directional purple panel (back) */}
        <div className="rl-card__panel" style={panelStyle} aria-hidden={!state.hovered}>
          <div className="relative h-full w-full p-6 md:p-8 flex flex-col justify-between">
            <span
              className={`font-editorial-mono text-[10.5px] text-[#F5B841] transition-opacity duration-300 ${state.hovered ? "opacity-100" : "opacity-0"}`}
              style={{ transitionDelay: state.hovered ? "100ms" : "0ms" }}
            >
              [ 0{i + 1} · {item.title.toUpperCase()} ]
            </span>
            <p
              className={`text-[14.5px] md:text-[15px] leading-[1.55] text-white/95 transition-all duration-500 max-h-[calc(100%-96px)] overflow-hidden ${state.hovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
              style={{ transitionDelay: state.hovered ? "160ms" : "0ms" }}
            >
              {item.body}
            </p>
            <div
              className={`flex items-center gap-1.5 text-white/70 transition-opacity duration-300 ${state.hovered ? "opacity-100" : "opacity-0"}`}
              style={{ transitionDelay: state.hovered ? "220ms" : "0ms" }}
            >
              <span className="font-editorial-mono text-[10.5px]">WHY IT MATTERS</span>
              <ArrowUpRight className="h-3.5 w-3.5" />
            </div>
          </div>
        </div>
      </button>
    </motion.div>
  );
}

function MobileSheet({ item, onClose }) {
  if (!item) return null;
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[80] bg-black/60"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="absolute inset-x-0 bottom-0 bg-[#0A0B10] text-[#F5F5F7] p-8 rounded-t-3xl"
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-6">
            <span className="font-editorial-mono text-[10.5px] text-[#F5B841]">
              [ {item.title.toUpperCase()} ]
            </span>
            <button onClick={onClose} className="text-white/70 hover:text-white" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <h3 className="font-editorial-display text-[32px] leading-[1.02] tracking-[-0.02em] mb-4">
            {item.title}
          </h3>
          <p className="text-[15px] leading-[1.6] text-white/90">{item.body}</p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function Challenges() {
  const [sheet, setSheet] = useState(null);

  return (
    <section
      data-testid="challenges-section"
      className="theme-editorial relative py-16 md:py-24 bg-white"
      aria-label="Cybersecurity challenges"
    >
      <div className="mx-auto max-w-[1280px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20">
          <div className="md:col-span-5">
            <div className="font-editorial-mono text-[10.5px] text-[#5B2A86] mb-4">CHALLENGES — 06</div>
            <h2 className="font-editorial-display text-[42px] md:text-[64px] leading-[0.98] tracking-[-0.028em] text-[#0B0B10]">
              It's harder than ever<br />
              to stay ahead of{" "}
              <span className="font-editorial-italic text-[#5B2A86]" style={{ fontStyle: "italic", fontWeight: 400 }}>cyber risk.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-[16px] md:text-[18px] leading-[1.55] text-[#4A4A55]">
              We get it. RockLadder addresses today's most pressing cybersecurity challenges — from
              AI-era exposure and regulatory complexity to the human, technological, and structural
              gaps that keep security teams reactive instead of resilient.
            </p>
          </div>
        </div>

        <div className="relative border-t border-l border-[rgba(17,17,17,0.10)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {CHALLENGES.map((c, i) => (
              <div key={c.key} className="border-b border-r border-[rgba(17,17,17,0.10)]">
                <DirectionalCard item={c} i={i} onOpenModal={setSheet} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <MobileSheet item={sheet} onClose={() => setSheet(null)} />
    </section>
  );
}
